import { FileText, Sparkles, Upload, X } from "lucide-react";
import { useRef, useState, type DragEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useJDSettings, useUploadJD } from "@/api/jd-queries";
import { useCan } from "@/auth/member";
import { Positions, Reading, UploadSteps } from "@/components/jd-upload-positions";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Callout, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { Segmented } from "@/components/ui/segmented";
import { cn } from "@/lib/utils";

/**
 * Upload a job description — DESIGN §20.5, §20.11, redesigned 2026-10-15 as three steps: add the JD,
 * the AI reads it, choose positions. A file or the pasted text of an email; one model call then finds
 * every position in it. The call is made inside the upload request — a person is waiting on the button —
 * so the positions are there when it returns; the list still polls for a source left queued or analysing
 * by a job queued some other way.
 */

type Mode = "file" | "paste";
const label = "mb-1 block text-[12.5px] font-medium";

export default function JobDescriptionUpload() {
  const canManage = useCan("manage_jd");
  const settings = useJDSettings();
  const upload = useUploadJD();
  const [params] = useSearchParams();
  const fileInput = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>(params.get("mode") === "paste" ? "paste" : "file");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [note, setNote] = useState("");
  const [sourceId, setSourceId] = useState<number | null>(null);

  const types = settings.data?.upload_types ?? ["pdf", "docx", "txt"];
  const ready = mode === "file" ? file !== null : text.trim().length > 0;

  function submit(force: boolean) {
    upload.mutate(
      {
        file: mode === "file" ? file : null,
        text: mode === "paste" ? text : "",
        title,
        clientName,
        note,
        force,
      },
      { onSuccess: (res) => setSourceId(res.source_id ?? null) },
    );
  }

  function drop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }

  function another() {
    setSourceId(null);
    setFile(null);
    setText("");
    setTitle("");
    setClientName("");
    setNote("");
    upload.reset();
  }

  if (!canManage) {
    return (
      <div className="p-4">
        <Callout>Uploading job descriptions is for Resource Managers and admins.</Callout>
      </div>
    );
  }

  const at = sourceId !== null ? 3 : upload.isPending ? 2 : 1;

  return (
    <div className="flex min-h-full flex-col gap-3.5 p-4">
      <nav className="text-bank-muted flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/jds" className="text-bank-accent-ink hover:underline">
          Job descriptions
        </Link>
        <span aria-hidden="true">›</span>
        <span>Upload</span>
      </nav>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <h1 className="text-base font-semibold tracking-tight">Upload a job description</h1>
        <UploadSteps at={at} />
      </div>

      {sourceId !== null ? (
        <Positions sourceId={sourceId} onAnother={another} />
      ) : upload.isPending ? (
        <Reading />
      ) : (
        <div className="flex flex-wrap items-start gap-3.5">
          <Panel className="max-w-[820px] min-w-0 flex-[999_1_560px]">
            <PanelHeader
              title="What the client sent"
              actions={
                <Segmented<Mode>
                  label="How to add the JD"
                  value={mode}
                  onChange={setMode}
                  options={[
                    { value: "file", label: "Upload a file" },
                    { value: "paste", label: "Paste text" },
                  ]}
                />
              }
            />
            <div className="flex flex-col gap-4 p-4">
              {mode === "file" ? (
                <>
                  <label
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={drop}
                    className={cn(
                      "flex cursor-pointer flex-col items-center gap-2 rounded-xl border-[1.5px] border-dashed px-4 py-7 text-center transition-colors",
                      dragging
                        ? "border-bank-accent bg-bank-accent-wash"
                        : "border-bank-border-strong bg-bank-surface-2 hover:bg-bank-surface-3",
                    )}
                  >
                    <span className="bg-bank-accent-wash text-bank-accent grid size-11 place-items-center rounded-full">
                      <Upload className="size-5" aria-hidden="true" />
                    </span>
                    <span className="text-[15px] font-semibold">
                      Drag the JD here, or{" "}
                      <span className="text-bank-accent-ink underline">choose a file</span>
                    </span>
                    <span className="text-bank-muted text-[12.5px]">
                      {types.map((t) => `.${t}`).join(", ")} · up to {settings.data?.upload_max_mb ?? 5} MB ·
                      one file can hold several positions
                    </span>
                    <span className="text-bank-muted text-xs">
                      A scanned PDF has no text to read — use Paste text for those.
                    </span>
                    <input
                      ref={fileInput}
                      type="file"
                      className="sr-only"
                      accept={types.map((t) => `.${t}`).join(",")}
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  {file && (
                    <div className="border-bank-border flex items-center gap-3 rounded-lg border px-3 py-2.5">
                      <FileText className="text-bank-accent size-5" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{file.name}</p>
                        <p className="text-bank-muted font-mono text-[11px]">
                          {Math.max(1, Math.round(file.size / 1024))} KB
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Remove the file"
                        onClick={() => {
                          setFile(null);
                          if (fileInput.current) fileInput.current.value = "";
                        }}
                      >
                        <X className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <label htmlFor="jd-text" className={label}>
                    The job description, as the client sent it
                  </label>
                  <Textarea
                    id="jd-text"
                    rows={12}
                    className="font-mono text-xs"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Paste it as it is — an email listing several roles, a portal page, a JD with questions at the end."
                  />
                </div>
              )}

              <details className="border-bank-border rounded-lg border">
                <summary className="cursor-pointer px-3 py-2.5 text-[13px] font-medium">
                  Add details{" "}
                  <span className="text-bank-muted font-normal">
                    (optional — the AI fills in what it can)
                  </span>
                </summary>
                <div className="grid gap-3 px-3 pt-1 pb-3.5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="jd-name" className={label}>
                      Name
                    </label>
                    <Input id="jd-name" value={title} onChange={(e) => setTitle(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="jd-client" className={label}>
                      Client
                    </label>
                    <Input
                      id="jd-client"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                    />
                    <p className="text-bank-muted mt-1 text-xs">
                      Hidden from members unless you choose to show it.
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="jd-note" className={label}>
                      Note for your team
                    </label>
                    <Input id="jd-note" value={note} onChange={(e) => setNote(e.target.value)} />
                  </div>
                </div>
              </details>

              <div className="border-bank-border flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t pt-3.5">
                <Button
                  variant="primary"
                  className="h-10 px-4.5 text-sm"
                  disabled={!ready}
                  onClick={() => submit(false)}
                >
                  <Sparkles className="size-4" aria-hidden="true" />
                  Analyse with AI
                </Button>
                <span className="text-bank-muted text-[12.5px]">
                  One AI call, usually under a minute. Nothing is published — every position waits for your
                  review.
                </span>
              </div>
              <ErrorNote error={upload.error} />
              {upload.data?.duplicate_of && !upload.data.source_id && (
                <Callout>
                  <p className="font-semibold">This exact text was uploaded before</p>
                  <p>Its positions are already in the list (upload #{upload.data.duplicate_of}).</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Link
                      to="/jds?status=all"
                      className="border-bank-border-strong bg-bank-surface text-bank-ink hover:bg-bank-surface-2 inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium"
                    >
                      Go to the list
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => submit(true)}>
                      Analyse it again anyway
                    </Button>
                  </div>
                </Callout>
              )}
            </div>
          </Panel>

          <Panel className="max-w-[380px] min-w-0 flex-[1_1_280px]">
            <PanelHeader title="What happens next" />
            <ol className="flex flex-col gap-3.5 p-4">
              {(
                [
                  [
                    "The AI reads it once.",
                    "It finds every position — an email can hold five — and the skills each asks for.",
                  ],
                  ["You keep or discard each position.", "Each kept one becomes its own job description."],
                  ["You check and publish.", "Members see nothing until you press Publish on the JD's page."],
                ] as const
              ).map(([head, body], i) => (
                <li key={head} className="flex gap-2.5 text-[13px]">
                  <span className="bg-bank-accent-wash text-bank-accent-ink grid size-[22px] shrink-0 place-items-center rounded-full font-mono text-[11.5px]">
                    {i + 1}
                  </span>
                  <span>
                    <strong>{head}</strong> {body}
                  </span>
                </li>
              ))}
            </ol>
            <p className="text-bank-muted border-bank-border border-t px-4 py-2.5 text-xs">
              No interview questions are needed to publish. You can add them before or after.
            </p>
          </Panel>
        </div>
      )}
    </div>
  );
}
